import { faImage } from "@fortawesome/pro-regular-svg-icons/faImage";
import { faVideo } from "@fortawesome/pro-regular-svg-icons/faVideo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, LinearProgress, Theme} from '@mui/material';
import { createStyles, withStyles, WithStyles } from "@mui/styles";
import * as pubApi from "api/Knowledge";
import { WidePicture } from "assets/appConfig";
import ButtonCancel from 'components/Buttons/ButtonCancel';
import { UploadCallback, UploadStatusChange } from 'components/FileUpload/AttachSelection';
import { ImageSelectionDialog } from 'components/ImageHandler';
import { ImageSelectionResult } from 'components/ImageHandler/ImageSelectionDialog';
import MuiRegularButton from 'components/MuiExtras/Button/MuiRegularButton';
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { AsyncUploader } from "lib/AsyncUploader";
import MediaUtils from 'lib/MediaUtils';
import * as LinkifyIt from "linkify-it";
import { AspectRatio } from 'models/ApiEnums';
import { AsyncUploadModel, ContentMetadataGetDto } from 'models/ApiModels';
import { AttachType } from 'models/AppModels';
import * as React from 'react';
import Dropzone from 'react-dropzone';
import { WithTranslation, withTranslation } from "react-i18next";
import { setTimeout } from 'timers';

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const styles = (theme: Theme) => createStyles({

    container: {
        lineHeight: "32px"
    },

    dropzone: {
        borderStyle: "dashed",
        borderWidth: 1,
        borderColor: theme.palette.grey[200],
        padding: "10px",
        "& > input[type=file]": {
            display: "none"
        }
    },

    progressBar: {
        width: "200px"
    },
});

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function searchLink(text: string): Promise<ContentMetadataGetDto> {
    const linkify = new LinkifyIt();
    const matches = linkify.match(text);

    return new Promise<ContentMetadataGetDto>((resolve) => {

        if (matches == null || matches.length == 0)
            resolve(null);
        else {
            pubApi.getMetadataFromUrl(matches[0].url)
                .then((meta) => {
                    resolve(meta);
                })
                .catch(() => {
                    resolve(null);
                });
        }
    });
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    isLoading: boolean;
    progressBar?: number;
    lastFileUploaded?: AsyncUploadModel;
    dialog: any;
}

interface IProps {
    acceptedTypes: AttachType[];
    dropzoneText?: string;
    readonly?: boolean;
    selectedType: AttachType;

    onUploadStatusChange?: UploadStatusChange;
    onUploadComplete?: UploadCallback;
    onClick: () => void;
}

type Props = IProps & WithStyles<typeof styles> & WithSnackbar & WithTranslation;

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

class HandleAttach extends React.Component<Props, IState> {

    private addBoxRef: HTMLElement;
    private asyncUploader: AsyncUploader;
    private fileInputRef: HTMLInputElement;

    ///
    constructor(props: Props) {
        super(props);
        this.state = { isLoading: false, lastFileUploaded: null, progressBar: null, dialog: null };

        this.asyncUploader = new
            AsyncUploader({
                onFileUploadProgress: this.loadAttachProgress,
                onFileUploadSuccess: this.loadAttachSuccess,
                onFileUploadError: this.loadAttachError,
                onFileStarted: this.loadAttachStarted,
            });
    }

    ///
    static defaultProps = {
        readonly: false
    };

    ///
    private loadImage = (image: ImageSelectionResult) => {
        if (image == null) {
            this.setState({ dialog: null });
        }
        else {
            var finalImage = image.source;

            this.setState({ isLoading: true, dialog: null, }, () => {

                this.asyncUploader.uploadImage(finalImage, AspectRatio.Wide);

            });
        }
    };

    ///
    private loadAttachStarted = () => {
        if (this.props.onUploadStatusChange) this.props.onUploadStatusChange("uploading");
    };

    ///
    private loadAttachProgress = (progress: number) => {
        this.setState({ progressBar: progress });
    };

    ///
    private loadAttachSuccess = (data: AsyncUploadModel) => {
        if (this.props.onUploadStatusChange) this.props.onUploadStatusChange("uploaded");

        if (data.success) {
            this.setState({ progressBar: null, lastFileUploaded: data, isLoading: false });
            if (this.props.onUploadComplete) this.props.onUploadComplete(data);
        }
        else {
            this.setState({ progressBar: null, lastFileUploaded: null, isLoading: false });
            this.props.snackbar.showMessage(this.props.t("Views.Publication.Components.HandleAttach.VideoUploadError"), "error");
        }
    };

    ///
    private loadAttachError = (thrown: any) => {
        if (this.props.onUploadStatusChange) this.props.onUploadStatusChange("canceled");

        if (this.asyncUploader.isCancel(thrown)) {
            this.props.snackbar.showMessage(this.props.t("Views.Publication.Components.HandleAttach.UploadCanceled"), "info");
        }
        else {
            this.props.snackbar.showMessage(this.props.t("Views.Publication.Components.HandleAttach.UploadError"), "error");
        }
        this.setState({ progressBar: null, lastFileUploaded: null, isLoading: false });
    };

    ///
    private loadAttach(e: File) {
        this.setState({ isLoading: true }, () => {

            const formData = new FormData();
            formData.append('file', e, e.name);
            formData.append('createThumbnailAspectRatio', AspectRatio[AspectRatio.Wide]);

            this.asyncUploader.uploadForm(formData);
        });
    }

    ///
    private handleAttachTypeButtonClick() {
        if (this.fileInputRef) this.fileInputRef.click();
    }

    ///
    private handleImageSelection(initialFile: File = null) {
        this.setState({
            dialog: (
                <ImageSelectionDialog
                    acceptedFiles={MediaUtils.AcceptImages()}
                    initialSource="library"
                    initialFile={initialFile}
                    allowEdit={false}
                    dimensions={WidePicture}
                    onClose={this.loadImage}
                />
            )
        });
    }

    ///
    private renderLeft() {
        const { acceptedTypes, selectedType, readonly, onClick } = this.props;

        if (readonly && selectedType == null)
            return null;

        return (
            <div>

                {
                    acceptedTypes.includes(AttachType.Video) && (selectedType == null || selectedType == AttachType.Video) &&
                    <>
                        <MuiRegularButton
                            simple
                            title={this.props.t("Common.AttachType.Video")}
                            color={selectedType == null ? "secondary" : "warning"}
                            onClick={() => {
                                selectedType == null
                                    ? this.handleAttachTypeButtonClick()
                                    : onClick();
                            }}>
                            <FontAwesomeIcon icon={faVideo} fixedWidth />
                        </MuiRegularButton>
                        <input
                            type="file"
                            accept={MediaUtils.AcceptAzureVideos().join(",")}
                            style={{ display: "none" }}
                            ref={(ref) => this.fileInputRef = ref}
                            onChange={
                                (e) => {
                                    this.loadAttach(e.target.files[0]);
                                    this.fileInputRef.value = null;
                                }
                            }
                        />
                    </>
                }

                {
                    acceptedTypes.includes(AttachType.Image) && (selectedType == null || selectedType == AttachType.Image) &&
                    <MuiRegularButton
                        simple
                        title={this.props.t("Common.AttachType.Image")}
                        color={selectedType == null ? "secondary" : "warning"}
                        onClick={() => {
                            selectedType == null
                                ? this.handleImageSelection()
                                : onClick();
                        }}>
                        <FontAwesomeIcon icon={faImage} fixedWidth />
                    </MuiRegularButton>
                }

            </div>
        );
    }

    ///
    private renderRight() {
        const { acceptedTypes, classes, readonly, selectedType } = this.props;
        const acceptFiles: string[] = [];
        if (acceptedTypes.includes(AttachType.Image)) acceptFiles.push(...MediaUtils.AcceptImages());
        if (acceptedTypes.includes(AttachType.Video)) acceptFiles.push(...MediaUtils.AcceptAzureVideos());

        if (readonly || acceptFiles.length == 0)
            return null;

        return (
            <div>

                {
                    !this.state.isLoading && selectedType == null && (
                        <Dropzone
                            accept={acceptFiles}
                            multiple={false}
                            onDropRejected={
                                () => this.props.snackbar.showMessage(this.props.t("Components.AttachSelection.FileNotAccept"), "error")
                            }
                            onDropAccepted={
                                (e) => {
                                    this.loadAttach(e[0]);
                                }
                            }
                        >
                            {
                                ({ getRootProps, getInputProps }) => {
                                    return (
                                        <div
                                            {...getRootProps()}
                                            className={classes.dropzone}
                                        >
                                            <input
                                                {...getInputProps()}
                                            />
                                            <span>{this.props.dropzoneText || this.props.t("Components.AttachSelection.DropzoneText")}</span>
                                        </div>
                                    );
                                }
                            }
                        </Dropzone>
                    )
                }

                {
                    this.state.progressBar != null
                        ? (
                            <>
                                <div className={classes.progressBar}>
                                    <LinearProgress variant="determinate" value={this.state.progressBar} />
                                </div>
                                <span>{this.state.progressBar + "%"}</span>
                                <ButtonCancel
                                    link
                                    simple
                                    onClick={
                                        () => {
                                            this.asyncUploader.cancel('Operation canceled by the user.');
                                        }
                                    }
                                />
                            </>
                        )
                        : null
                }
            </div>
        );
    }

    ///
    public componentDidUpdate() {
        setTimeout(() => this.addBoxRef && this.addBoxRef.scrollIntoView(false), 100);
    }

    ///
    public render() {
        const { classes } = this.props;
        return (
            <>
                <div style={{ width: "100%" }}>
                    <Grid container className={classes.container}>
                        <Grid item xs>
                            {this.renderLeft()}
                        </Grid>
                        <Grid item >
                            {this.renderRight()}
                        </Grid>
                    </Grid>
                </div>
                {this.state.dialog}
            </>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(styles)(HandleAttach)));